import { HttpClient } from '@angular/common/http';
import { IAppResultFile } from '@app/core/model/appresults/appresult-file';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BytesFilter } from '@app/core/utilities/filters/bytes.filter';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';

export enum AppResultFileSearchColumnFields {
    Name = 'Name',
    Path = 'Path',
    Analysis = 'Analysis',
    AppResult = 'AppResult',
    Project = 'Project',
    Size = 'Size',
    Created = 'Created'
}

export class AppResultFileSearchGridOptionsFormatter extends SearchGridOptionsFormatter<AppResultFileSearchColumnFields> {
    public projectFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
        public resourceDictionaryService: SearchResourceDictionaryService,
        public bsshDatePipe: BsshDatePipe,
        public http: HttpClient
    ) {
        super(resourceDictionaryService, SearchResourceType.AppResultFile);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
        return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
            params: {
                limit: '50',
                scope: 'projects',
                sortBy: 'ModifiedOn',
                sortDir: 'desc',
                query: arg ? `Project.Name:"${arg}"` : ''
            },
            withCredentials: true
        });
    }

    updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
        return this.getProjectFilterOptions(arg).pipe(
          map((response) => {
            return this.toProjectFilterOptions(response);
          })
        );
    }

    bindFilterUpdateCallbacksToCurrentContext() {
        this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
    }

    toProjectFilterOptions(result): FilterOption[] {
        const projects: [] = result.Response.Items;
        const filteroptions: FilterOption[] = [];
        projects.forEach((item: any) => {
          const filterOption: FilterOption = {
            displayName: item.Project.Name,
            filterValue: item.Project.Id
          };
          filteroptions.push(filterOption);

        });
        return filteroptions;
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<AppResultFileSearchColumnFields> {
        const inclusiveColumns: AppResultFileSearchColumnFields[] = [
            AppResultFileSearchColumnFields.Name,
            AppResultFileSearchColumnFields.AppResult,
            AppResultFileSearchColumnFields.Size,
            AppResultFileSearchColumnFields.Created
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<AppResultFileSearchColumnFields>[] = [
            {
              colId: AppResultFileSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions() {
        const defaultColumnDefinitions = [
            {
                headerName: 'Name',
                colId: AppResultFileSearchColumnFields.Name,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE,
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {

                        const resource: IAppResultFile = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        const fileContainerPath = BsApiEndPoints.getAppResultFileRoute(resource);

                        const tooltipText = !this.isRowSelectable(params.node) ? this.getUnselectableRowMessage(params.node) : null;
                        const cellRendererParams = DataTableUtilities.getRadLinkParams(
                            resource.Name,
                            fileContainerPath,
                            tooltipText
                        );

                        return cellRendererParams;
                    } else {
                        return {};
                    }
                },
            },
            {
                headerName: 'Path',
                colId: AppResultFileSearchColumnFields.Path,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE,
                cellRendererSelector: (params) => {
                    if(params.data) {
                        const resource: IAppResultFile = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        return DataTableUtilities.getSpanRenderer(resource.Path);
                    } else {
                        return DataTableUtilities.getSpanRenderer();
                    }
                },
            },
            {
                headerName: 'Analysis',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const resource: IAppResultFile = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        const appResult = resource.ParentAppResult;
                        const appSession = appResult.AppSession;

                        const cellRendererParams = {
                          linkText: appSession.Name,
                          linkUrl: BsApiEndPoints.getAnalysesRoute(appSession.Id),
                          target: '_blank'
                        };
                        return cellRendererParams;
                    } else {
                        return {};
                    }
                },
                colId: AppResultFileSearchColumnFields.Analysis,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'App Result',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const resource: IAppResultFile = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        const appResult = resource.ParentAppResult;
                        const appSession = appResult.AppSession;
                        return DataTableUtilities.getRadLinkParams(
                            appSession.Name,
                            BsApiEndPoints.getAppResultRoute(appSession.Id, appResult.Id)
                        );
                    } else {
                        return {};
                    }
                },
                colId: AppResultFileSearchColumnFields.AppResult,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Project',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const resource: IAppResultFile = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        const project = resource.ParentProjects[0];
                        return DataTableUtilities.getRadLinkParams(
                            project.Name,
                            BsApiEndPoints.getProjectDetailsRoute(project.Id)
                        );
                    } else {
                        return {};
                    }
                },
                colId: AppResultFileSearchColumnFields.Project,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback,
            },
            {
                headerName: 'Size',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                    return BytesFilter(Number(resource.Size));
                  },
                colId: AppResultFileSearchColumnFields.Size,
                cellRenderer: 'span',
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.SIZE
            },
            {
                headerName: 'Created',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                    return this.bsshDatePipe.transform(resource.DateCreated);
                  },
                filterActive: false,
                sortable: false,
                colId: AppResultFileSearchColumnFields.Created,
                cellRenderer: 'span',
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE
              },
        ];
        return defaultColumnDefinitions;
    }
}
